<template>
  <div class="app-container">
    <el-container>
      <!-- 顶部工具栏 -->
      <el-header class="app-header">
        <div class="header-left">
          <el-icon :size="24" color="#409EFF"><Connection /></el-icon>
          <span class="app-title">DAPI - API管理工具</span>
        </div>
        <div class="header-right">
          <el-button @click="saveAll">
            <el-icon><Check /></el-icon>
            保存
          </el-button>
          <el-button v-if="storageMode === 'file'" type="primary" @click="selectWorkspace">
            <el-icon><FolderOpened /></el-icon>
            {{ workspaceName || '选择工作目录' }}
          </el-button>
          <el-button @click="showSettings = true">
            <el-icon><Setting /></el-icon>
          </el-button>
        </div>
      </el-header>

      <el-container class="main-container">
        <!-- 左侧边栏 -->
        <div class="sidebar-wrapper" :style="{ width: sidebarWidth + 'px' }">
          <el-aside :width="sidebarWidth + 'px'" class="sidebar">
            <el-tabs v-model="leftTab" type="border-card" class="sidebar-tabs">
              <el-tab-pane label="项目" name="projects">
                <ProjectTree
            :projects="projects"
            :current-api="currentApi"
            @select-api="handleSelectApi"
            @select-node="handleSelectNode"
            @add-project="handleAddProject"
            @add-folder="handleAddFolder"
            @add-api="handleAddApi"
            @delete-node="handleDeleteNode"
            @edit-node="handleEditNode"
            @export-node="handleExportNode"
            @import-project="handleImportProject"
            @import-all="handleImportAll"
            @export-all="handleExportAll"
            @import-openapi-all="handleImportOpenApiAll"
            @export-all-openapi="handleExportAllOpenApi"
            @import-openapi-project="handleImportOpenApiProject"
            @export-node-openapi="handleExportNodeOpenApi"
            @node-sort="handleNodeSort"
          />
              </el-tab-pane>
              <el-tab-pane label="环境" name="environment">
                <EnvironmentManager
                  v-model="currentEnvId"
                  :environments="environments"
                  @update:environments="handleUpdateEnvironments"
                  @change="handleEnvChange"
                />
              </el-tab-pane>
            </el-tabs>
          </el-aside>
          <!-- 拖拽调整宽度的手柄 -->
          <div class="resize-handle" @mousedown="startResize"></div>
        </div>

        <!-- 主内容区 -->
        <el-main class="main-content">
          <ApiWorkspace
            v-if="currentApi"
            :api="currentApi"
            :key="currentApi.id"
            :environments="environments"
            :current-env-id="currentEnvId"
            @update="handleUpdateApi"
            @send-request="handleSendRequest"
          />
          <div v-else class="empty-state">
            <el-empty description="请选择一个接口或创建新接口">
              <el-button type="primary" @click="handleAddApi">创建接口</el-button>
            </el-empty>
          </div>
        </el-main>
      </el-container>
    </el-container>

    <!-- 设置对话框 -->
    <SettingsDialog
      v-model="showSettings"
      :mode="storageMode"
      @update:mode="handleModeChange"
      @export-data="handleExportAllData"
      @import-data="handleImportAllData"
      @clear-data="handleClearAllData"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import ProjectTree from './components/ProjectTree.vue'
import ApiWorkspace from './components/ApiWorkspace.vue'
import { fileStorage } from './utils/fileStorage'
import { indexedDBStorage } from './utils/indexedDBStorage'
import { toPostmanCollection, fromPostmanCollection, downloadAsFile, importFromFile, findNodeById, removeNodeById } from './utils/postmanAdapter'
import { toOpenApiDocument, fromOpenApiDocument, downloadOpenApiFile, importOpenApiFromFile } from './utils/openapiAdapter'
import EnvironmentManager from './components/EnvironmentManager.vue'
import SettingsDialog from './components/SettingsDialog.vue'

// 状态
const workspaceName = ref('')
const projects = ref([])
const currentApi = ref(null)
const selectedNode = ref(null)
const leftTab = ref('projects')
const environments = ref([])
const currentEnvId = ref('')
const storageMode = ref('indexeddb') // 'indexeddb' 或 'file'
const showSettings = ref(false)
const sidebarWidth = ref(280)
const isResizing = ref(false)

// 存储键名
const STORAGE_KEY = 'dapi_settings'
const PROJECTS_KEY = 'dapi_projects'
const ENVIRONMENTS_KEY = 'dapi_environments'
const SIDEBAR_WIDTH_KEY = 'dapi_sidebar_width'

// 选择工作目录
const selectWorkspace = async () => {
  try {
    const success = await fileStorage.selectDirectory()
    if (success) {
      workspaceName.value = fileStorage.getDirectoryName()
      await loadProjects()
      await loadEnvironments()
      ElMessage.success('工作目录已选择')
    }
  } catch (error) {
    ElMessage.error('选择目录失败: ' + error.message)
  }
}

// 加载设置
const loadSettings = () => {
  try {
    const settings = localStorage.getItem(STORAGE_KEY)
    if (settings) {
      const parsed = JSON.parse(settings)
      storageMode.value = parsed.storageMode || 'indexeddb'
    }
  } catch (error) {
    console.log('加载设置失败:', error)
  }
}

// 保存设置
const saveSettings = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      storageMode: storageMode.value
    }))
    console.log('设置已保存:', storageMode.value)
  } catch (error) {
    console.log('保存设置失败:', error)
  }
}

// 处理存储模式切换
const handleModeChange = async (mode) => {
  const oldMode = storageMode.value

  // 如果模式相同，不做处理
  if (oldMode === mode) return

  // 如果旧模式是 file 且没有选择目录，跳过保存
  if (oldMode === 'file' && !fileStorage.hasDirectory()) {
    // 直接切换，不保存旧数据
  } else {
    // 先保存当前数据到旧模式
    await saveAll()
  }

  // 清空当前显示的数据
  projects.value = []
  environments.value = []
  currentEnvId.value = ''
  currentApi.value = null
  selectedNode.value = null

  // 切换模式
  storageMode.value = mode
  saveSettings()

  // 从新模式加载数据
  await loadProjects()
  await loadEnvironments()

  ElMessage.success(`已切换到 ${mode === 'indexeddb' ? 'IndexedDB' : '本地文件'} 模式，数据已同步`)
}

// 加载侧边栏宽度
const loadSidebarWidth = () => {
  try {
    const width = localStorage.getItem(SIDEBAR_WIDTH_KEY)
    if (width) {
      sidebarWidth.value = parseInt(width)
    }
  } catch (error) {
    console.log('加载侧边栏宽度失败:', error)
  }
}

// 保存侧边栏宽度
const saveSidebarWidth = () => {
  try {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.value.toString())
  } catch (error) {
    console.log('保存侧边栏宽度失败:', error)
  }
}

// 开始调整大小
const startResize = (e) => {
  isResizing.value = true
  const startX = e.clientX
  const startWidth = sidebarWidth.value

  const handleMouseMove = (e) => {
    if (!isResizing.value) return
    const diff = e.clientX - startX
    const newWidth = Math.max(200, Math.min(500, startWidth + diff))
    sidebarWidth.value = newWidth
  }

  const handleMouseUp = () => {
    isResizing.value = false
    saveSidebarWidth()
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

// 加载项目数据
const loadProjects = async () => {
  try {
    let data
    if (storageMode.value === 'file') {
      data = await fileStorage.readFile('projects.json')
      if (data) {
        projects.value = JSON.parse(data)
      }
    } else {
      data = await indexedDBStorage.getData(PROJECTS_KEY)
      if (data) {
        projects.value = data
      }
    }
  } catch (error) {
    console.log('没有现有项目数据')
    projects.value = []
  }
}

// 加载环境数据
const loadEnvironments = async () => {
  try {
    let parsed
    if (storageMode.value === 'file') {
      const data = await fileStorage.readFile('environments.json')
      if (data) {
        parsed = JSON.parse(data)
      }
    } else {
      parsed = await indexedDBStorage.getData(ENVIRONMENTS_KEY)
    }

    if (parsed) {
      environments.value = parsed.environments || []
      currentEnvId.value = parsed.currentEnvId || ''
    }
  } catch (error) {
    console.log('加载环境数据失败:', error)
    environments.value = []
  }
}

// 保存所有数据
const saveAll = async () => {
  if (storageMode.value === 'file' && !fileStorage.hasDirectory()) {
    // 文件模式下如果没有选择目录，提示用户选择
    ElMessage.warning('请先选择工作目录')
    // 自动打开目录选择
    await selectWorkspace()
    return
  }
  try {
    if (storageMode.value === 'file') {
      await fileStorage.writeFile('projects.json', JSON.stringify(projects.value, null, 2))
      await fileStorage.writeFile('environments.json', JSON.stringify({
        environments: environments.value,
        currentEnvId: currentEnvId.value
      }, null, 2))
    } else {
      await indexedDBStorage.saveData(PROJECTS_KEY, projects.value)
      await indexedDBStorage.saveData(ENVIRONMENTS_KEY, {
        environments: environments.value,
        currentEnvId: currentEnvId.value
      })
    }
    saveSettings()
    ElMessage.success('保存成功')
  } catch (error) {
    ElMessage.error('保存失败: ' + error.message)
  }
}

// 更新环境
const handleUpdateEnvironments = (envs) => {
  environments.value = envs
  saveAll()
}

// 环境切换
const handleEnvChange = (envId) => {
  currentEnvId.value = envId
  saveAll()
}

// 添加项目
const handleAddProject = () => {
  ElMessageBox.prompt('请输入项目名称', '新建项目', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
  }).then(({ value }) => {
    if (value) {
      const project = {
        id: Date.now().toString(),
        type: 'project',
        name: value,
        description: '',
        children: []
      }
      projects.value.push(project)
      saveAll()
    }
  }).catch(() => {
    // 取消操作，不做处理
  })
}

// 添加文件夹
const handleAddFolder = ({ parentId, name }) => {
  const parent = findNodeById(projects.value, parentId)
  if (parent) {
    if (!parent.children) {
      parent.children = []
    }
    parent.children.push({
      id: Date.now().toString(),
      type: 'folder',
      name: name,
      description: '',
      children: []
    })
    saveAll()
    ElMessage.success('文件夹创建成功')
  }
}

// 添加接口
const handleAddApi = (parentNode) => {
  let parent

  if (parentNode && parentNode.id) {
    // 从菜单点击，使用传入的节点
    parent = findNodeById(projects.value, parentNode.id)
  } else if (selectedNode.value && selectedNode.value.id) {
    // 使用当前选中的节点
    const selected = findNodeById(projects.value, selectedNode.value.id)
    if (selected && (selected.type === 'project' || selected.type === 'folder')) {
      parent = selected
    }
  }

  // 如果没有找到父节点，使用第一个项目
  if (!parent && projects.value.length > 0) {
    parent = projects.value[0]
  }

  if (!parent) {
    ElMessage.warning('请先创建项目')
    return
  }

  if (!parent.children) {
    parent.children = []
  }

  const api = {
    id: Date.now().toString(),
    type: 'api',
    name: '新建接口',
    method: 'GET',
    url: '',
    headers: [{ key: '', value: '', description: '' }],
    params: [{ key: '', value: '', type: 'string', required: false, description: '' }],
    body: {
      type: 'none',
      content: '',
      formData: [],
      file: null,
      fileName: ''
    },
    responseParams: [],
    responseHeaders: [],
    responseBody: {
      type: 'none',
      content: ''
    },
    description: ''
  }

  parent.children.push(api)
  currentApi.value = api
  saveAll()
}

// 选择接口
const handleSelectApi = (api) => {
  currentApi.value = api
}

// 选择节点（项目/文件夹/接口）
const handleSelectNode = (node) => {
  selectedNode.value = node
}

// 更新接口
const handleUpdateApi = (updatedApi) => {
  const api = findNodeById(projects.value, updatedApi.id)
  if (api) {
    Object.assign(api, updatedApi)
  }
}

// 删除节点（支持批量删除）
const handleDeleteNode = ({ type, id, ids }) => {
  if (type === 'batch' && ids) {
    // 批量删除
    for (const apiId of ids) {
      removeNodeById(projects.value, apiId)
      if (currentApi.value && currentApi.value.id === apiId) {
        currentApi.value = null
      }
    }
    saveAll()
  } else if (type === 'single' && id) {
    // 单个删除
    ElMessageBox.confirm('确定要删除这个节点吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(() => {
      removeNodeById(projects.value, id)
      if (currentApi.value && currentApi.value.id === id) {
        currentApi.value = null
      }
      saveAll()
      ElMessage.success('删除成功')
    }).catch(() => {
      // 取消操作，不做处理
    })
  }
}

// 编辑节点（项目或文件夹）
const handleEditNode = (data) => {
  const node = findNodeById(projects.value, data.id)
  if (node) {
    node.name = data.name
    node.description = data.description
    saveAll()
    ElMessage.success('修改成功')
  }
}

// 发送请求
const handleSendRequest = async () => {
  // 请求逻辑在 ApiWorkspace 组件中处理
}

// 导出节点（项目、文件夹或接口）
const handleExportNode = (node) => {
  const collection = toPostmanCollection([node], {
    name: node.name,
    description: node.description || 'Exported from DAPI'
  })
  
  downloadAsFile(collection, `${node.name}.postman_collection.json`)
  ElMessage.success('导出成功')
}

// 导入 Postman 到项目
const handleImportProject = (project) => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.json'
  input.onchange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      const data = await importFromFile(file)

      if (!data.info || !data.item) {
        ElMessage.error('无效的 Postman Collection 文件')
        return
      }

      const importedProjects = fromPostmanCollection(data)

      if (importedProjects.length === 0) {
        ElMessage.warning('文件中没有找到数据')
        return
      }

      // 将导入的数据合并到当前项目
      const targetProject = findNodeById(projects.value, project.id)
      if (targetProject) {
        if (!targetProject.children) {
          targetProject.children = []
        }

        // 合并所有导入的项目/文件夹到当前项目
        for (const importedProject of importedProjects) {
          if (importedProject.children) {
            targetProject.children.push(...importedProject.children)
          }
        }

        saveAll()
        ElMessage.success('导入成功')
      }

    } catch (error) {
      ElMessage.error('导入失败: ' + error.message)
    }
  }
  input.click()
}

// 导出节点为 OpenAPI（项目、文件夹或接口）
const handleExportNodeOpenApi = (node) => {
  // 单个接口需包裹成项目结构，便于复用 toOpenApiDocument
  let projectsToExport
  let name = node.name
  if (node.type === 'api') {
    projectsToExport = [{
      id: 'openapi-export-tmp',
      type: 'project',
      name: node.name,
      description: '',
      children: [node]
    }]
  } else {
    projectsToExport = [node]
  }

  const doc = toOpenApiDocument(projectsToExport, {
    name: name,
    version: '1.0.0',
    description: node.description || 'Exported from DAPI'
  })

  const pathCount = Object.keys(doc.paths || {}).length
  if (pathCount === 0) {
    ElMessage.warning('该节点下没有可导出的 HTTP 接口（WebSocket/STOMP 等非 HTTP 方法将被跳过）')
    return
  }

  downloadOpenApiFile(doc, `${node.name}.openapi.json`)
  ElMessage.success(`导出成功，共 ${pathCount} 个路径`)
}

// 导入 OpenAPI 到项目（合并到当前项目）
const handleImportOpenApiProject = (project) => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.json,.yaml,.yml'
  input.onchange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      const data = await importOpenApiFromFile(file)

      if (!data.openapi || !data.paths) {
        ElMessage.error('无效的 OpenAPI 文档：缺少 openapi 或 paths 字段')
        return
      }

      const importedProjects = fromOpenApiDocument(data)
      if (importedProjects.length === 0) {
        ElMessage.warning('文件中没有找到可导入的接口')
        return
      }

      const apiCount = (importedProjects[0].children || []).length
      if (apiCount === 0) {
        ElMessage.warning('文件中没有找到可导入的接口')
        return
      }

      // 将导入的接口合并到当前项目
      const targetProject = findNodeById(projects.value, project.id)
      if (targetProject) {
        if (!targetProject.children) {
          targetProject.children = []
        }
        targetProject.children.push(...importedProjects[0].children)
        saveAll()
        ElMessage.success(`成功导入 ${apiCount} 个接口`)
      }
    } catch (error) {
      ElMessage.error('导入失败: ' + error.message)
    }
  }
  input.click()
}

// 处理节点排序
const handleNodeSort = ({ dragNode, dropNode, dropType }) => {
  // 找到拖拽节点和放置节点的父级
  const findParent = (nodes, nodeId) => {
    for (const node of nodes) {
      if (node.id === nodeId) {
        return { parent: nodes, index: nodes.indexOf(node) }
      }
      if (node.children) {
        const result = findParent(node.children, nodeId)
        if (result) return result
      }
    }
    return null
  }

  // 找到拖拽节点的位置
  const dragResult = findParent(projects.value, dragNode.id)
  if (!dragResult) return

  const { parent: dragParent, index: dragIndex } = dragResult

  // 移除拖拽节点
  const [removedNode] = dragParent.splice(dragIndex, 1)

  if (dropType === 'inner') {
    // 放入目标节点内部
    const dropResult = findParent(projects.value, dropNode.id)
    if (dropResult) {
      const { parent: dropParent } = dropResult
      const targetNode = dropParent.find(n => n.id === dropNode.id)
      if (targetNode) {
        if (!targetNode.children) {
          targetNode.children = []
        }
        targetNode.children.push(removedNode)
      }
    }
  } else {
    // before 或 after，在同一层级插入
    const dropResult = findParent(projects.value, dropNode.id)
    if (dropResult) {
      const { parent: dropParent, index: dropIndex } = dropResult
      const insertIndex = dropType === 'before' ? dropIndex : dropIndex + 1
      dropParent.splice(insertIndex, 0, removedNode)
    }
  }

  // 保存数据
  saveAll()
  ElMessage.success('排序已更新')
}

// 导入全部（创建新项目）
const handleImportAll = () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.json'
  input.onchange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      const data = await importFromFile(file)

      if (!data.info || !data.item) {
        ElMessage.error('无效的 Postman Collection 文件')
        return
      }

      const importedProjects = fromPostmanCollection(data)

      if (importedProjects.length === 0) {
        ElMessage.warning('文件中没有找到数据')
        return
      }

      // 添加为新项目
      projects.value.push(...importedProjects)
      saveAll()
      ElMessage.success(`成功导入 ${importedProjects.length} 个项目`)

    } catch (error) {
      ElMessage.error('导入失败: ' + error.message)
    }
  }
  input.click()
}

// 导出全部
const handleExportAll = () => {
  if (projects.value.length === 0) {
    ElMessage.warning('没有可导出的项目')
    return
  }

  const collection = toPostmanCollection(projects.value, {
    name: workspaceName.value || 'DAPI Collection',
    description: 'Exported from DAPI'
  })

  downloadAsFile(collection, `${workspaceName.value || 'collection'}.postman_collection.json`)
  ElMessage.success('导出成功')
}

// 导入 OpenAPI 文档（创建新项目）
const handleImportOpenApiAll = () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.json,.yaml,.yml'
  input.onchange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      const data = await importOpenApiFromFile(file)

      // 简单校验 OpenAPI 文档
      if (!data.openapi || !data.paths) {
        ElMessage.error('无效的 OpenAPI 文档：缺少 openapi 或 paths 字段')
        return
      }

      const importedProjects = fromOpenApiDocument(data)

      if (importedProjects.length === 0) {
        ElMessage.warning('文件中没有找到可导入的接口')
        return
      }

      // 统计导入的接口数
      const apiCount = (importedProjects[0].children || []).length
      if (apiCount === 0) {
        ElMessage.warning('文件中没有找到可导入的接口')
        return
      }

      projects.value.push(...importedProjects)
      saveAll()
      ElMessage.success(`成功导入 ${apiCount} 个接口`)
    } catch (error) {
      ElMessage.error('导入失败: ' + error.message)
    }
  }
  input.click()
}

// 导出全部为 OpenAPI
const handleExportAllOpenApi = () => {
  if (projects.value.length === 0) {
    ElMessage.warning('没有可导出的项目')
    return
  }

  const doc = toOpenApiDocument(projects.value, {
    name: workspaceName.value || 'DAPI Collection',
    version: '1.0.0',
    description: 'Exported from DAPI'
  })

  const pathCount = Object.keys(doc.paths || {}).length
  if (pathCount === 0) {
    ElMessage.warning('没有可导出的 HTTP 接口（WebSocket/STOMP 等非 HTTP 方法将被跳过）')
    return
  }

  downloadOpenApiFile(doc, `${workspaceName.value || 'collection'}.openapi.json`)
  ElMessage.success(`导出成功，共 ${pathCount} 个路径`)
}

// 导出所有数据（设置中使用）
const handleExportAllData = async () => {
  try {
    const data = {
      projects: projects.value,
      environments: {
        environments: environments.value,
        currentEnvId: currentEnvId.value
      },
      exportTime: new Date().toISOString()
    }
    downloadAsFile(data, `dapi_backup_${Date.now()}.json`)
    ElMessage.success('数据导出成功')
  } catch (error) {
    ElMessage.error('导出失败: ' + error.message)
  }
}

// 导入所有数据（设置中使用）
const handleImportAllData = async (file) => {
  try {
    const text = await file.text()
    const data = JSON.parse(text)

    if (data.projects) {
      projects.value = data.projects
    }
    if (data.environments) {
      environments.value = data.environments.environments || []
      currentEnvId.value = data.environments.currentEnvId || ''
    }

    await saveAll()
    ElMessage.success('数据导入成功')
  } catch (error) {
    ElMessage.error('导入失败: ' + error.message)
  }
}

// 清空所有数据
const handleClearAllData = async () => {
  try {
    projects.value = []
    environments.value = []
    currentEnvId.value = ''
    currentApi.value = null
    selectedNode.value = null

    if (storageMode.value === 'file') {
      await fileStorage.writeFile('projects.json', '[]')
      await fileStorage.writeFile('environments.json', JSON.stringify({ environments: [], currentEnvId: '' }))
    } else {
      await indexedDBStorage.saveData(PROJECTS_KEY, [])
      await indexedDBStorage.saveData(ENVIRONMENTS_KEY, { environments: [], currentEnvId: '' })
    }

    ElMessage.success('数据已清空')
  } catch (error) {
    ElMessage.error('清空失败: ' + error.message)
  }
}

// 初始化
onMounted(() => {
  // 加载设置
  loadSettings()

  // 加载侧边栏宽度
  loadSidebarWidth()

  // 根据存储模式加载数据
  if (storageMode.value === 'file') {
    // 尝试恢复之前选择的目录
    fileStorage.restoreDirectory().then(async (restored) => {
      if (restored) {
        workspaceName.value = fileStorage.getDirectoryName()
        await loadProjects()
        await loadEnvironments()
      } else {
        // 没有恢复目录，提示用户选择
        ElMessage.warning('请重新选择工作目录')
      }
    })
  } else {
    // IndexedDB 模式，直接加载数据
    loadProjects()
    loadEnvironments()
  }
})
</script>

<style scoped>
@import './assets/style/global.less';
.app-container {
  height: 100vh;
  overflow: hidden;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  border-bottom: 1px solid #dcdfe6;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 4px;
}

.app-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.header-right {
  display: flex;
  gap: 4px;
}

.main-container {
  height: calc(100vh - 60px);
}

.sidebar-wrapper {
  display: flex;
  position: relative;
  height: 100%;
}

.sidebar {
  background-color: #f5f7fa;
  border-right: 1px solid #dcdfe6;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.resize-handle {
  width: 4px;
  height: 100%;
  cursor: col-resize;
  background-color: transparent;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 10;
  transition: background-color 0.2s;
}

.resize-handle:hover,
.resize-handle:active {
  background-color: #409eff;
}

.sidebar-tabs {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sidebar-tabs .el-tabs__content {
  flex: 1;
  overflow: auto;
  padding: 0;
}

/* 自定义滚动条样式 */
.sidebar-tabs .el-tabs__content::-webkit-scrollbar {
  width: 4px;
}

.sidebar-tabs .el-tabs__content::-webkit-scrollbar-thumb {
  background-color: rgba(144, 147, 153, 0.4);
  border-radius: 2px;
}

.sidebar-tabs .el-tabs__content::-webkit-scrollbar-track {
  background-color: transparent;
}

.main-content {
  background-color: #fff;
}

/* 自定义滚动条样式 */
.main-content::-webkit-scrollbar {
  width: 4px;
}

.main-content::-webkit-scrollbar-thumb {
  background-color: rgba(144, 147, 153, 0.4);
  border-radius: 2px;
}

.main-content::-webkit-scrollbar-track {
  background-color: transparent;
}

.empty-state {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
